import { DependencyList, useEffect, useState } from 'react';
import { Object3D, Scene } from 'three';

import { listenObjectChanges } from '@spazious/dollhouse-three';
import { useEventCallback } from '@spazious/react-hooks';

/**
 * Get all the objects that match the filter function provided
 * listening to changes in the scene
 */
export const useSceneObjects = (
  scene: Scene,
  filter: (node: Object3D) => boolean,
  deps: DependencyList = []
) => {
  const [sceneObjects, setSceneObjects] = useState<Object3D[]>([]);

  const update = useEventCallback(() => {
    const nodes: Object3D[] = [];
    scene?.traverse((node) => {
      if (filter(node)) {
        nodes.push(node);
      }
    });
    setSceneObjects(nodes);
  });

  const addedNode = useEventCallback((node: Object3D) => {
    if (filter(node)) {
      setSceneObjects((prev) => [...prev, node]);
    }
  });

  const removedNode = useEventCallback((node: Object3D) => {
    if (filter(node)) {
      setSceneObjects((prev) => prev.filter((n) => n !== node));
    }
  });

  useEffect(
    () => (scene ? listenObjectChanges(scene, addedNode, removedNode) : () => {}),
    [addedNode, removedNode, scene]
  );

  useEffect(() => {
    update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return [sceneObjects, update] as const;
};
